// ═══════════════════════════════════════════════════════════════════
//  ImportStepperV39 — Import Produk (Fasa 2, stepper webapp)
//  RUPA v3.9 sahaja. Modal 3 langkah: Setup → Scrape → Review.
//
//  Webapp = pusat arahan; extension = kuli. Aliran:
//   • Setup  : user tampal link + semak extension aktif (SESAAT_PING/PONG).
//   • Scrape : hantar SCRAPE_TIKTOK_BATCH ke extension melalui jambatan
//              SESAAT_CHROME_BRIDGE; dengar progress yang di-relay content.ts
//              (SCRAPE_TIKTOK_PROGRESS / _CAPTCHA / _STATUS) → papar bar.
//   • Review : senarai hasil (READY/PENDING/gagal) + pilih profil & group →
//              Simpan (LibraryView tulis kad, guna konvensyen sedia ada).
//
//  ⚠️ Enjin scrape SEBENAR ada dalam extension (Fasa 1). Modal ni tak scrape
//     sendiri — ia cuma arah + papar. Jadi ia perlu extension AKTIF.
//  ⚠️ Pintu "Scrape Showcase" (auto kutip ID) menyusul (Fasa 2b) — guna modal
//     yang sama, cuma langkah Setup beza.
// ═══════════════════════════════════════════════════════════════════
import { useState, useEffect, useMemo } from 'react';
import { evaluateProductData } from '../services/productReadyService';
import { extractTikTokProductId } from '../utils/productDisplayId';

// Hantar satu arahan ke extension melalui jambatan sedia ada (content.ts relay).
function bridgeSend(payload) {
    const id = `imp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    window.postMessage({ type: 'SESAAT_CHROME_BRIDGE', id, payload }, '*');
    return id;
}

// Hantar arahan + TUNGGU balasan (untuk COLLECT_SHOWCASE_IDS yang pulang senarai produk).
function bridgeRequest(payload, timeoutMs = 300000) {
    return new Promise((resolve) => {
        const id = `imp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const onMsg = (e) => {
            if (e.data?.type === 'SESAAT_CHROME_BRIDGE_RESPONSE' && e.data.id === id) {
                window.removeEventListener('message', onMsg);
                clearTimeout(tmr);
                resolve(e.data.response);
            }
        };
        const tmr = setTimeout(() => { window.removeEventListener('message', onMsg); resolve(null); }, timeoutMs);
        window.addEventListener('message', onMsg);
        window.postMessage({ type: 'SESAAT_CHROME_BRIDGE', id, payload }, '*');
    });
}

// Pecah teks jadi senarai link TikTok yang unik.
function parseLinks(txt) {
    return [...new Set(
        String(txt || '')
            .split(/[\s,]+/)
            .map(s => s.trim())
            .filter(s => /tiktok\.com/i.test(s))
    )];
}

// ── Warna group (sama dgn extension Import + My Products) ──
const GROUP_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#0ea5e9', '#8b5cf6', '#ef4444', '#64748b'];

// Spec: "Kunci : Nilai" satu baris satu (dipakai borang Edit Manual).
function parseSpecLines(text) {
    const out = {};
    String(text || '').split(/\r?\n/).forEach(line => {
        const m = line.trim().match(/^([^:：]{1,40})\s*[:：]\s*(.+)$/);
        if (m) out[m[1].trim()] = m[2].trim();
    });
    return out;
}
// Ulasan: satu baris satu ulasan.
function parseReviewLines(text) {
    return String(text || '').split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 2).map(l => ({ text: l }));
}
// Link papar produk (showcase) + link sumber scrape (PDP) — untuk butang Copy per kad.
function buildViewProductLink(data, sourceUrl) {
    const pid = data?.productId || extractTikTokProductId(data?.resolvedUrl || '') || extractTikTokProductId(sourceUrl || '');
    return pid ? `https://shop.tiktok.com/view/product/${pid}?region=MY&locale=en` : (data?.resolvedUrl || sourceUrl || '');
}
function scrapeSourceUrl(data, sourceUrl) {
    const pid = data?.productId || extractTikTokProductId(data?.resolvedUrl || '') || extractTikTokProductId(sourceUrl || '');
    return pid ? `https://www.tiktok.com/shop/my/pdp/${pid}` : '';
}

const STYLE = `
.imp-overlay{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:9999;padding:16px;}
.imp-modal{background:var(--s-card);color:var(--s-ink);border:1px solid var(--s-line);border-radius:16px;width:min(720px,100%);max-height:90vh;display:flex;flex-direction:column;box-shadow:var(--s-shadow-modal);overflow:hidden;}
.imp-head{display:flex;align-items:center;gap:10px;padding:16px 18px;border-bottom:1px solid var(--s-line);}
.imp-title{font-size:15px;font-weight:700;flex:1;}
.imp-x{width:30px;height:30px;border-radius:8px;border:0;background:var(--s-fill);color:var(--s-ink-2);cursor:pointer;font-size:16px;line-height:1;}
.imp-x:hover{background:var(--s-fill-hover);}
.imp-steps{display:flex;align-items:center;gap:6px;padding:12px 18px;flex-wrap:wrap;}
.imp-pill{font-size:12px;font-weight:600;padding:4px 12px;border-radius:999px;background:var(--s-fill);color:var(--s-ink-3);}
.imp-pill.on{background:var(--s-ink);color:var(--s-card);}
.imp-pill.done{background:var(--s-ok-soft,var(--s-fill));color:var(--s-ok);}
.imp-sep{color:var(--s-ink-3);font-size:13px;}
.imp-body{padding:16px 18px;overflow-y:auto;flex:1;}
.imp-modes{display:flex;gap:6px;margin-bottom:14px;}
.imp-mode{flex:1;font-size:12px;font-weight:600;padding:8px;border-radius:9px;border:1px solid var(--s-line);background:var(--s-fill);color:var(--s-ink-2);cursor:pointer;}
.imp-mode.on{background:var(--s-ink);color:var(--s-card);border-color:var(--s-ink);}
.imp-lbl{font-size:11px;font-weight:600;color:var(--s-ink-2);margin-bottom:5px;display:block;}
.imp-ta{width:100%;min-height:130px;background:var(--s-fill);border:1px solid var(--s-line);border-radius:10px;padding:10px 12px;color:var(--s-ink);font-size:13px;font-family:inherit;resize:vertical;box-sizing:border-box;}
.imp-ta:focus{outline:none;border-color:var(--s-ink-3);}
.imp-sel{width:100%;background:var(--s-fill);border:1px solid var(--s-line);border-radius:10px;padding:9px 12px;color:var(--s-ink);font-size:13px;box-sizing:border-box;}
.imp-ext{display:flex;align-items:center;gap:7px;font-size:12px;margin-top:12px;}
.imp-dot{width:8px;height:8px;border-radius:99px;flex:none;}
.imp-note{font-size:12px;color:var(--s-ink-2);line-height:1.55;background:var(--s-fill);border-radius:10px;padding:10px 12px;margin-top:12px;}
.imp-reconnect{display:flex;flex-direction:column;gap:9px;align-items:flex-start;background:var(--s-warn-soft,var(--s-fill));border:1px solid var(--s-warn);border-radius:10px;padding:10px 12px;margin-top:8px;font-size:12px;line-height:1.5;color:var(--s-ink);}
.imp-refresh{display:inline-flex;align-items:center;gap:7px;font-size:13px;font-weight:600;padding:8px 14px;border-radius:9px;border:1px solid var(--s-ink);background:var(--s-ink);color:var(--s-card);cursor:pointer;}
.imp-refresh:hover{filter:brightness(1.08);}
.imp-prog{height:8px;background:var(--s-fill);border-radius:99px;overflow:hidden;margin:10px 0;}
.imp-prog>div{height:100%;background:var(--s-ink);transition:width .3s;}
.imp-row{display:flex;align-items:center;gap:8px;font-size:12px;background:var(--s-fill);border-radius:8px;padding:7px 10px;margin-bottom:5px;}
.imp-row .nm{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.imp-badge{font-size:10px;font-weight:700;padding:2px 8px;border-radius:999px;}
.imp-foot{display:flex;align-items:center;gap:10px;padding:14px 18px;border-top:1px solid var(--s-line);}
.imp-btn{font-size:13px;font-weight:600;padding:9px 18px;border-radius:10px;border:1px solid var(--s-line);background:var(--s-card);color:var(--s-ink);cursor:pointer;}
.imp-btn:hover{background:var(--s-fill);}
.imp-btn.primary{background:var(--s-ink);color:var(--s-card);border-color:var(--s-ink);}
.imp-btn.primary:hover{filter:brightness(1.08);}
.imp-btn.ok{background:var(--s-ok);color:var(--s-card);border-color:var(--s-ok);}
.imp-btn:disabled{opacity:.45;cursor:not-allowed;}
.imp-cap{background:var(--s-warn-soft,var(--s-fill));border:1px solid var(--s-warn);border-radius:10px;padding:10px 12px;font-size:12px;color:var(--s-ink);margin-bottom:12px;display:flex;gap:8px;align-items:flex-start;}
.imp-scraping{display:flex;align-items:center;gap:9px;font-size:13px;font-weight:600;color:var(--s-ink);background:var(--s-fill);border-radius:10px;padding:10px 12px;margin-bottom:10px;}
.imp-scraping .nm{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.imp-spin{width:15px;height:15px;flex:none;border:2px solid var(--s-line-strong,var(--s-line));border-top-color:var(--s-ink);border-radius:50%;animation:imp-rot .7s linear infinite;}
@keyframes imp-rot{to{transform:rotate(360deg)}}
/* Review per-produk */
.imp-bulkbar{display:flex;align-items:center;gap:10px;padding:8px 10px;background:var(--s-fill);border:1px solid var(--s-line);border-radius:10px;margin-bottom:8px;}
.imp-checkl{display:flex;align-items:center;gap:6px;font-size:11px;font-weight:700;color:var(--s-ink-2);cursor:pointer;}
.imp-cards{display:flex;flex-direction:column;gap:8px;max-height:340px;overflow-y:auto;margin-bottom:4px;}
.imp-card{background:var(--s-card);border:1px solid var(--s-line);border-radius:12px;padding:10px;}
.imp-card.err{background:var(--s-stop-soft,var(--s-fill));border-color:var(--s-stop);}
.imp-cardtop{display:flex;gap:10px;align-items:flex-start;}
.imp-thumb{width:52px;height:52px;object-fit:cover;border-radius:8px;background:var(--s-fill);flex:none;display:flex;align-items:center;justify-content:center;font-size:18px;}
.imp-thumb.ph{color:var(--s-ink-3);}
.imp-idrow{display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
.imp-id{font-size:10px;font-weight:800;color:var(--s-select,#6366f1);white-space:nowrap;}
.imp-name{font-size:12.5px;font-weight:700;color:var(--s-ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin-top:2px;}
.imp-meta{font-size:10.5px;color:var(--s-ink-2);margin-top:3px;font-weight:600;}
.imp-err{font-size:10.5px;color:var(--s-stop);font-weight:600;margin-top:3px;}
.imp-xrow{border:0;background:transparent;color:var(--s-stop);font-size:13px;font-weight:800;cursor:pointer;padding:2px 4px;flex:none;}
.imp-copyrow{display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;}
.imp-erractions{display:flex;gap:6px;margin-top:8px;}
.imp-chip{font-size:10.5px;font-weight:800;padding:5px 10px;border-radius:7px;border:1px solid var(--s-line);background:var(--s-fill);color:var(--s-ink-2);cursor:pointer;}
.imp-chip:hover{background:var(--s-fill-hover);}
.imp-chip.brand{color:var(--s-card);background:var(--s-ink);border-color:var(--s-ink);}
.imp-chip.warn{color:var(--s-warn);background:var(--s-warn-soft,var(--s-fill));border-color:var(--s-warn);}
.imp-chip.stop{color:var(--s-stop);background:var(--s-stop-soft,var(--s-fill));border-color:var(--s-stop);}
.imp-chip:disabled{opacity:.5;cursor:not-allowed;}
.imp-warn{margin-top:8px;background:var(--s-warn-soft,var(--s-fill));border:1px solid var(--s-warn);border-radius:8px;padding:8px 10px;font-size:11px;color:var(--s-ink);line-height:1.5;}
.imp-gpill{width:100%;display:flex;align-items:center;gap:6px;margin-top:8px;padding:6px 10px;border-radius:8px;font-size:10.5px;font-weight:800;cursor:pointer;background:var(--s-fill);color:var(--s-ink-3);border:1px dashed var(--s-line-strong,var(--s-line));}
.imp-gpill .nm{flex:1;text-align:left;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.imp-gmenu{background:var(--s-card);border:1px solid var(--s-line);border-radius:10px;box-shadow:var(--s-shadow-pop,0 12px 32px rgba(0,0,0,.15));overflow:hidden;margin-top:6px;}
.imp-gmi{width:100%;display:flex;align-items:center;gap:8px;padding:8px 10px;font-size:11px;font-weight:700;color:var(--s-ink);background:transparent;border:0;cursor:pointer;text-align:left;}
.imp-gmi:hover{background:var(--s-fill);}
.imp-gmi .nm{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.imp-gdot{width:10px;height:10px;border-radius:5px;flex:none;}
.imp-gadd{border-top:1px solid var(--s-line);padding:8px;display:flex;flex-direction:column;gap:6px;}
.imp-gnew{width:100%;padding:7px 10px;font-size:11px;font-weight:800;color:var(--s-ink);background:var(--s-fill);border:1px dashed var(--s-line-strong,var(--s-line));border-radius:8px;cursor:pointer;}
.imp-ginput{width:100%;padding:6px 8px;font-size:11px;font-weight:600;border-radius:6px;border:1px solid var(--s-line);background:var(--s-fill);color:var(--s-ink);box-sizing:border-box;}
.imp-gcolors{display:flex;gap:4px;flex-wrap:wrap;}
.imp-gcolor{width:18px;height:18px;border-radius:9px;border:0;cursor:pointer;padding:0;}
.imp-editovl{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:10000;}
.imp-editbox{background:var(--s-card);border:1px solid var(--s-line);border-radius:14px;max-width:420px;width:92%;max-height:86vh;display:flex;flex-direction:column;box-shadow:var(--s-shadow-modal,0 20px 60px rgba(0,0,0,.3));}
.imp-edithead{padding:14px 16px;border-bottom:1px solid var(--s-line);}
.imp-editbody{padding:16px;overflow-y:auto;flex:1;display:flex;flex-direction:column;gap:10px;}
.imp-editfoot{padding:12px;border-top:1px solid var(--s-line);display:flex;gap:8px;justify-content:flex-end;}
.imp-fld{width:100%;padding:8px 10px;font-size:12px;font-weight:600;border-radius:8px;border:1px solid var(--s-line);background:var(--s-fill);color:var(--s-ink);box-sizing:border-box;font-family:inherit;}
.imp-fldl{font-size:10px;font-weight:800;color:var(--s-ink-2);text-transform:uppercase;letter-spacing:.05em;display:block;margin-bottom:4px;}
.imp-toast{position:fixed;left:50%;bottom:20px;transform:translateX(-50%);z-index:10001;background:var(--s-ink);color:var(--s-card);padding:8px 16px;border-radius:10px;font-size:12px;font-weight:600;box-shadow:var(--s-shadow-float,0 8px 24px rgba(0,0,0,.3));}
@media (max-width:640px){.imp-modal{max-height:96vh;}}
`;

export default function ImportStepperV39({
    open,
    onClose,
    profiles = [],
    groups = [],
    existingIds = null,   // Set<string> productId yang dah ada dalam My Products
    isAdmin = false,
    // ⚠️ Akaun destinasi datang dari HALAMAN (kad profile yang sedang dibuka),
    // BUKAN dipilih di dalam wizard ini — arahan user 2026-08-21. Pemilih akaun
    // yang dulu ada di langkah Review sudah dibuang; nilai di sini hanya untuk
    // (a) menapis senarai group dan (b) memberitahu pengguna produk akan
    // mendarat di mana. Kosong = tiada akaun (produk duduk dalam "Semua produk").
    profileId = '',
    onSave,               // async ({items}) => {saved}
    onAddGroup,           // async (name, color) => {id} | null — cipta group baru terus dari Review
    tabPlatform = null,   // ★ 2026-10-03 — nod <TabPlatformImport> (TikTok | Shopee) di atas langkah Setup.
                          //   Stepper ini kekal TikTok SAHAJA; tab Shopee = ImportShopeeV39.jsx.
    t,
}) {
    const T = (k, d, v) => {
        if (!t) return d;
        const s = t(k, v);
        return (s === k) ? d : s;
    };

    const [step, setStep] = useState('setup');       // setup | scrape | review
    const [linksText, setLinksText] = useState('');
    const [extAlive, setExtAlive] = useState(null);  // null=semak, true, false
    const [results, setResults] = useState([]);      // {sourceUrl, status:'pending'|'scraping'|'success'|'error', data, error}
    const [captcha, setCaptcha] = useState(null);    // {sourceUrl, message}
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState('');
    // ── Mod Setup: 'link' (tampal) atau 'showcase' (kutip dari Seller showcase) ──
    const [setupMode, setSetupMode] = useState('link');
    const [collecting, setCollecting] = useState(false);
    const [showcaseProgress, setShowcaseProgress] = useState(null); // {page, found}
    const [showcasePreview, setShowcasePreview] = useState(null);   // [{id,name,shop}]
    const [showcaseSel, setShowcaseSel] = useState(() => new Set());
    const [showcaseSearch, setShowcaseSearch] = useState('');
    const [collectError, setCollectError] = useState('');
    // Julat page showcase (1 page = 20 produk). Kosong "hingga" = kutip semua page.
    const [showcaseStart, setShowcaseStart] = useState('1');
    const [showcaseEnd, setShowcaseEnd] = useState('');

    // ── Review: kawalan per-produk (parity dengan Import di extension) ──
    const [rowGroup, setRowGroup] = useState({});               // sourceUrl → groupId | null
    const [removedUrls, setRemovedUrls] = useState(() => new Set());
    const [selectedUrls, setSelectedUrls] = useState(() => new Set());
    const [dataEdits, setDataEdits] = useState({});             // sourceUrl → data (override selepas Edit Manual)
    const [groupMenuFor, setGroupMenuFor] = useState(null);     // sourceUrl | '__bulk__' | null
    const [showAddGroup, setShowAddGroup] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupColor, setNewGroupColor] = useState(GROUP_COLORS[0]);
    const [savingGroup, setSavingGroup] = useState(false);
    const [sessionGroups, setSessionGroups] = useState([]);     // group dicipta dlm sesi ni (papar segera sblm prop update)
    const [editUrl, setEditUrl] = useState(null);               // sourceUrl kad yang sedang di-edit
    const [editFields, setEditFields] = useState({});
    const [retryingUrls, setRetryingUrls] = useState(() => new Set());
    const [rowToast, setRowToast] = useState('');               // mesej ringkas (salin link dsb)

    // Nota: modal ini di-mount SEGAR setiap kali dibuka (LibraryView gate `importOpen`),
    // jadi nilai awal useState di atas sudah menjadi "reset" — tiada effect reset diperlukan.

    // ── Semak extension aktif (SESAAT_PING/PONG). Status diset dalam callback
    //    (PONG / timeout), bukan badan effect — elak cascading render. ──
    useEffect(() => {
        if (!open) return;
        let got = false;
        const timers = [];
        const onMsg = (e) => {
            if (e.data?.type === 'SESAAT_PONG') { got = true; setExtAlive(true); }
        };
        window.addEventListener('message', onMsg);
        // Ping beberapa kali — service worker extension kerap "sejuk" bila halaman
        // baru dibuka, jadi PONG pertama boleh lambat. Cuba 3× sebelum lapor gagal
        // (elak false "tak dikesan" sedangkan extension sebenarnya OK).
        const ping = () => { if (!got) window.postMessage({ type: 'SESAAT_PING' }, '*'); };
        ping();
        timers.push(setTimeout(ping, 900));
        timers.push(setTimeout(ping, 1800));
        timers.push(setTimeout(() => { if (!got) setExtAlive(false); }, 2800));
        return () => { window.removeEventListener('message', onMsg); timers.forEach(clearTimeout); };
    }, [open]);

    // ── Dengar progress scrape (di-relay content.ts) ──
    useEffect(() => {
        if (!open || step !== 'scrape') return;
        const onMsg = (e) => {
            const m = e.data;
            if (!m || typeof m !== 'object') return;
            if (m.type === 'SCRAPE_TIKTOK_PROGRESS') {
                setResults(prev => {
                    const i = prev.findIndex(r => r.sourceUrl === m.sourceUrl);
                    const patch = { status: m.status, data: m.data, error: m.error };
                    if (i >= 0) { const c = [...prev]; c[i] = { ...c[i], ...patch }; return c; }
                    return [...prev, { sourceUrl: m.sourceUrl, ...patch }];
                });
                if (m.status !== 'scraping') {
                    setCaptcha(c => (c && c.sourceUrl === m.sourceUrl) ? null : c);
                }
            } else if (m.type === 'SCRAPE_TIKTOK_CAPTCHA') {
                setCaptcha(m.active ? { sourceUrl: m.sourceUrl, message: m.message } : null);
            } else if (m.type === 'SCRAPE_TIKTOK_STATUS') {
                // Transisi ke Review dalam callback (bukan effect) — elak set-state-in-effect.
                if (m.status === 'complete' || m.status === 'cancelled') setStep('review');
            }
        };
        window.addEventListener('message', onMsg);
        return () => window.removeEventListener('message', onMsg);
    }, [open, step]);

    // ── Kutip ID dari Seller showcase (extension buka tab, kumpul, pulang senarai) ──
    useEffect(() => {
        if (!collecting) return;
        const onMsg = (e) => {
            if (e.data?.type === 'SHOWCASE_PROGRESS') setShowcaseProgress({ page: e.data.page, found: e.data.found });
        };
        window.addEventListener('message', onMsg);
        return () => window.removeEventListener('message', onMsg);
    }, [collecting]);

    const collectShowcase = async () => {
        if (collecting) return;
        // Julat page (corak sama Option 2 extension): startPage + endPage (kosong = semua).
        const startPage = Math.max(1, parseInt(showcaseStart, 10) || 1);
        const endRaw = String(showcaseEnd).trim();
        const parsedEnd = parseInt(endRaw, 10);
        if (endRaw && (isNaN(parsedEnd) || parsedEnd < startPage)) {
            setCollectError(T('import.showcase.pageErr', `Page "hingga" mesti lebih besar atau sama dengan page mula (${startPage})`, { start: startPage }));
            return;
        }
        const maxPages = endRaw ? (parsedEnd - startPage + 1) : 0; // 0 = sampai habis
        setCollecting(true);
        setCollectError('');
        setShowcaseProgress({ page: 0, found: 0 });
        setShowcasePreview(null);
        try {
            const res = await bridgeRequest({ type: 'COLLECT_SHOWCASE_IDS', maxPages, startPage });
            if (!res) { setCollectError(T('import.showcase.timeout', 'Tiada respons dari extension — pastikan ia aktif.')); return; }
            if (!res.success) { setCollectError(res.error || T('import.showcase.fail', 'Gagal kutip showcase.')); return; }
            const arr = Array.isArray(res.products) ? res.products : [];
            if (arr.length === 0) { setCollectError(T('import.showcase.empty', 'Tiada produk dijumpai — pastikan anda log masuk TikTok Seller.')); return; }
            setShowcasePreview(arr);
            setShowcaseSel(new Set(arr.map(p => p.id)));
        } catch (e) {
            setCollectError(String((e && e.message) || e));
        } finally {
            setCollecting(false);
            setShowcaseProgress(null);
        }
    };

    const toggleSel = (id) => {
        setShowcaseSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    };
    const showcaseFiltered = (showcasePreview || []).filter(p =>
        !showcaseSearch.trim() || String(p.name || '').toLowerCase().includes(showcaseSearch.trim().toLowerCase()));
    const allFilteredSel = showcaseFiltered.length > 0 && showcaseFiltered.every(p => showcaseSel.has(p.id));
    const toggleAllFiltered = () => {
        setShowcaseSel(prev => {
            const n = new Set(prev);
            if (allFilteredSel) showcaseFiltered.forEach(p => n.delete(p.id));
            else showcaseFiltered.forEach(p => n.add(p.id));
            return n;
        });
    };

    const startScrape = () => {
        const urls = setupMode === 'link'
            ? parseLinks(linksText)
            : (showcasePreview || []).filter(p => showcaseSel.has(p.id)).map(p => `https://www.tiktok.com/shop/my/pdp/${p.id}`);
        if (!urls.length) return;
        setResults(urls.map(u => ({ sourceUrl: u, status: 'pending' })));
        setCaptcha(null);
        setStep('scrape');
        bridgeSend({ type: 'SCRAPE_TIKTOK_BATCH', urls, includeKalodata: !!isAdmin, enableRetry: true });
    };

    const cancelScrape = () => {
        bridgeSend({ type: 'SCRAPE_TIKTOK_CANCEL' });
        setStep('review');
    };

    // ── Model baris Review — SATU senarai (parity extension): gabung hasil scrape,
    //    suntingan manual (Edit Manual), group per-produk, dan buang per-produk. ──
    const rows = useMemo(() => {
        return results
            .filter(r => !removedUrls.has(r.sourceUrl))
            .map(r => {
                const edited = dataEdits[r.sourceUrl];
                const sd = edited || r.data || null;
                const status = edited ? 'success' : r.status;   // Edit Manual tukar error → success
                const productId = sd ? (sd.productId || extractTikTokProductId(sd.resolvedUrl || sd.sourceUrl || r.sourceUrl) || '') : '';
                const link = productId ? `https://shop.tiktok.com/view/product/${productId}` : (r.sourceUrl || '');
                let ready = false, missing = [];
                if (status === 'success' && sd) {
                    const ev = evaluateProductData(sd, { name: sd.name, imageUrl: sd.imageUrl || (sd.gallery && sd.gallery[0]) || '', link });
                    ready = ev.ready; missing = ev.missing;
                }
                const exists = !!(existingIds && productId && existingIds.has(String(productId)));
                return { sourceUrl: r.sourceUrl, data: sd, status, error: r.error, productId, link, ready, missing, exists, group: rowGroup[r.sourceUrl] ?? null };
            });
    }, [results, removedUrls, dataEdits, rowGroup, existingIds]);

    const successRows = rows.filter(r => r.status === 'success' && r.data);
    const readyCount = successRows.filter(r => r.ready).length;

    const failedItems = results.filter(r => r.status === 'error');
    const total = results.length;
    const done = results.filter(r => r.status === 'success' || r.status === 'error').length;
    const okCount = results.filter(r => r.status === 'success').length;
    const currentScraping = results.find(r => r.status === 'scraping'); // produk yang sedang diproses (batch berturutan)

    // Group dipapar = prop + yang dicipta dalam sesi ni (papar segera sebelum prop sync).
    const allGroups = useMemo(() => {
        const seen = new Set((groups || []).map(g => g.id));
        return [...(groups || []), ...sessionGroups.filter(g => !seen.has(g.id))];
    }, [groups, sessionGroups]);

    // Group dipapar dalam menu per-produk: IKUT akaun yang dipilih. Bila
    // satu akaun dipilih → hanya group akaun itu + group berkongsi (tiada
    // akaun); belum pilih akaun → hanya group berkongsi. Inilah pembetulan
    // "stepper tunjuk group semua profil" (arahan user 2026-08-20).
    // ★ 2026-10-03 (aduan user) — diselaraskan dengan My Products (`groupsForProduct`,
    //   peraturan 22 Ogos): akaun dibuka → HANYA group milik akaun itu; tiada akaun →
    //   hanya group tanpa akaun. Dahulu group tanpa akaun turut muncul di bawah setiap akaun.
    const visibleGroups = allGroups.filter(g => (profileId ? g.profileId === profileId : !g.profileId));

    // Nama akaun destinasi — dipakai oleh nota baca-sahaja di langkah Review.
    const namaProfilDestinasi = profileId
        ? (profiles.find(p => p.id === profileId)?.name || '')
        : '';

    const handleSave = async () => {
        if (!onSave || saving || successRows.length === 0) return;
        setSaving(true);
        try {
            const res = await onSave({
                items: successRows.map(r => ({
                    data: r.data, ready: r.ready, link: r.link, productId: r.productId,
                    groupId: r.group || null,   // ★ group per-produk
                    pendingReason: r.ready ? null : `Data tidak lengkap: ${(r.missing || []).join(', ')}`,
                })),
            });
            const n = (res && typeof res.saved === 'number') ? res.saved : successRows.length;
            setSavedMsg(T('import.saved', `${n} kad disimpan ke My Products`, { n }));
            setTimeout(() => { onClose?.(); }, 1200);
        } catch {
            setSavedMsg(T('import.saveFail', 'Gagal simpan — cuba lagi'));
            setSaving(false);
        }
    };

    // ═══════════ Review handlers: group / buang / pilih / edit / retry / copy ═══════════
    const assignGroup = (urls, groupId) => {
        setRowGroup(prev => { const n = { ...prev }; urls.forEach(u => { n[u] = groupId; }); return n; });
        setGroupMenuFor(null); setShowAddGroup(false);
    };
    const removeRows = (urls) => {
        setRemovedUrls(prev => { const n = new Set(prev); urls.forEach(u => n.add(u)); return n; });
        setSelectedUrls(prev => { const n = new Set(prev); urls.forEach(u => n.delete(u)); return n; });
        setGroupMenuFor(null);
    };
    const toggleSelect = (url) => setSelectedUrls(prev => { const n = new Set(prev); if (n.has(url)) n.delete(url); else n.add(url); return n; });
    const selectableUrls = successRows.map(r => r.sourceUrl);
    const allSelected = selectableUrls.length > 0 && selectableUrls.every(u => selectedUrls.has(u));
    const toggleSelectAll = () => setSelectedUrls(allSelected ? new Set() : new Set(selectableUrls));

    const createGroupInline = async (urls) => {
        const name = newGroupName.trim();
        if (!name || !onAddGroup) return;
        // Dua akaun berbeza boleh ada group bernama sama — semak dalam skop akaun ini sahaja.
        if (visibleGroups.some(g => String(g.name).toLowerCase() === name.toLowerCase())) {
            setRowToast(T('import.group.dup', 'Nama group dah wujud')); return;
        }
        setSavingGroup(true);
        try {
            const res = await onAddGroup(name, newGroupColor);
            const id = res && res.id;
            if (id) {
                // Group baharu jadi milik akaun destinasi (LibraryView menulis `profileId` yang sama).
                setSessionGroups(prev => [...prev, { id, name, color: newGroupColor, ...(profileId ? { profileId } : {}) }]);
                assignGroup(urls, id);
                setNewGroupName(''); setNewGroupColor(GROUP_COLORS[0]); setShowAddGroup(false);
            } else {
                setRowToast(T('import.group.addFail', 'Gagal tambah group'));
            }
        } catch {
            setRowToast(T('import.group.addFail', 'Gagal tambah group'));
        } finally { setSavingGroup(false); }
    };

    const openEdit = (r) => {
        setEditUrl(r.sourceUrl);
        const d = r.data || {};
        setEditFields({
            name: d.name || '', price: d.price || '', originalPrice: d.originalPrice || '',
            discount: d.discount || '', category: d.category || '', description: d.description || '',
            imageUrl: d.imageUrl || d.rawImageUrl || '',
            specsText: Object.entries(d.specifications || {}).map(([k, v]) => `${k} : ${v}`).join('\n'),
            reviewsText: (Array.isArray(d.topReviews) ? d.topReviews : []).map(rv => String(rv?.text || '').trim()).filter(Boolean).join('\n'),
        });
    };
    const saveEdit = () => {
        if (!editUrl) return;
        const f = editFields;
        if (!f.name?.trim() || !f.price?.trim()) { setRowToast(T('import.edit.need', 'Nama dan Harga wajib diisi')); return; }
        const base = dataEdits[editUrl] || (results.find(r => r.sourceUrl === editUrl)?.data) || {};
        const pid = base.productId || extractTikTokProductId(base.resolvedUrl || '') || extractTikTokProductId(editUrl) || '';
        const data = {
            ...base, source: 'tiktok', sourceUrl: editUrl, productId: pid,
            name: f.name.trim(), price: f.price.trim(),
            originalPrice: f.originalPrice?.trim() || undefined, discount: f.discount?.trim() || undefined,
            category: f.category?.trim() || undefined, description: f.description?.trim() || undefined,
            imageUrl: f.imageUrl?.trim() || undefined,
        };
        const specs = parseSpecLines(f.specsText || '');
        if (Object.keys(specs).length) data.specifications = { ...(base.specifications || {}), ...specs };
        const revs = parseReviewLines(f.reviewsText || '');
        if (revs.length) data.topReviews = [...(Array.isArray(base.topReviews) ? base.topReviews : []), ...revs];
        setDataEdits(prev => ({ ...prev, [editUrl]: data }));
        setEditUrl(null);
        setRowToast(T('import.edit.saved', '✓ Produk dikemas kini'));
    };

    // Retry satu produk gagal — scrape semula url tunggal. Dengar progress khusus url ni
    //   (listener utama hanya aktif masa step 'scrape', jadi Review perlu listener sendiri).
    const retryOne = (url) => {
        setRetryingUrls(prev => new Set(prev).add(url));
        setResults(prev => prev.map(r => r.sourceUrl === url ? { ...r, status: 'scraping', error: undefined } : r));
        const onMsg = (e) => {
            const m = e.data;
            if (!m || m.type !== 'SCRAPE_TIKTOK_PROGRESS' || m.sourceUrl !== url) return;
            setResults(prev => prev.map(r => r.sourceUrl === url ? { ...r, status: m.status, data: m.data ?? r.data, error: m.error } : r));
            if (m.status === 'success' || m.status === 'error') {
                window.removeEventListener('message', onMsg);
                setRetryingUrls(prev => { const n = new Set(prev); n.delete(url); return n; });
            }
        };
        window.addEventListener('message', onMsg);
        setTimeout(() => { window.removeEventListener('message', onMsg); setRetryingUrls(prev => { const n = new Set(prev); n.delete(url); return n; }); }, 180000);
        bridgeSend({ type: 'SCRAPE_TIKTOK_BATCH', urls: [url], includeKalodata: !!isAdmin, enableRetry: true });
    };

    const copyText = async (txt, okMsg) => {
        try { await navigator.clipboard.writeText(txt); setRowToast(okMsg); }
        catch { setRowToast(T('import.copyFail', 'Gagal salin')); }
    };

    // Auto-lap toast ringkas.
    useEffect(() => { if (!rowToast) return; const t2 = setTimeout(() => setRowToast(''), 2200); return () => clearTimeout(t2); }, [rowToast]);

    if (!open) return null;

    const badge = (bg, ink, txt) => (
        <span className="imp-badge" style={{ background: bg, color: ink }}>{txt}</span>
    );

    // Menu group (inline-expand — elak clipping dlm body yang ber-scroll).
    const groupMenu = (urls) => (
        <div className="imp-gmenu">
            <button className="imp-gmi" onClick={() => assignGroup(urls, null)}>⊘ {T('import.group.none', 'Tiada Group')}</button>
            {visibleGroups.map(g => (
                <button key={g.id} className="imp-gmi" onClick={() => assignGroup(urls, g.id)}>
                    <span className="imp-gdot" style={{ background: g.color }} />
                    <span className="nm">{g.name}</span>
                </button>
            ))}
            {visibleGroups.length === 0 && (
                <div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--s-ink-3)', fontStyle: 'italic' }}>{profileId
                    ? T('import.group.noneForProfile', 'Akaun ini belum ada group')
                    : T('import.group.pickProfile', 'Pilih akaun dahulu untuk group khas akaun')}</div>
            )}
            {onAddGroup && (
                <div className="imp-gadd">
                    {!showAddGroup ? (
                        <button className="imp-gnew" onClick={() => setShowAddGroup(true)}>+ {T('import.group.new', 'Group Baru')}</button>
                    ) : (
                        <>
                            <input className="imp-ginput" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder={T('import.group.name', 'Nama group')} autoFocus />
                            <div className="imp-gcolors">
                                {GROUP_COLORS.map(c => (
                                    <button key={c} className="imp-gcolor" style={{ background: c, outline: newGroupColor === c ? '2px solid var(--s-ink)' : '2px solid transparent' }} onClick={() => setNewGroupColor(c)} />
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button className="imp-btn primary" style={{ flex: 1, padding: '6px 8px', fontSize: 11 }} disabled={savingGroup || !newGroupName.trim()} onClick={() => createGroupInline(urls)}>
                                    {savingGroup ? T('import.group.saving', 'Menyimpan…') : T('import.group.saveUse', 'Simpan & Guna')}
                                </button>
                                <button className="imp-btn" style={{ padding: '6px 8px', fontSize: 11 }} onClick={() => { setShowAddGroup(false); setNewGroupName(''); }}>{T('import.cancel', 'Batal')}</button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="imp-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && step !== 'scrape') onClose?.(); }}>
            <style>{STYLE}</style>
            <div className="imp-modal">
                <div className="imp-head">
                    <span className="imp-title">{T('import.title', 'Import Produk')}</span>
                    {step !== 'scrape' && (
                        <button className="imp-x" onClick={() => onClose?.()} aria-label="Tutup">✕</button>
                    )}
                </div>

                <div className="imp-steps">
                    <span className={`imp-pill ${step === 'setup' ? 'on' : 'done'}`}>1 · {T('import.step.setup', 'Setup')}</span>
                    <span className="imp-sep">›</span>
                    <span className={`imp-pill ${step === 'scrape' ? 'on' : (step === 'review' ? 'done' : '')}`}>2 · {T('import.step.scrape', 'Scrape')}</span>
                    <span className="imp-sep">›</span>
                    <span className={`imp-pill ${step === 'review' ? 'on' : ''}`}>3 · {T('import.step.review', 'Review')}</span>
                </div>

                <div className="imp-body">
                    {/* ── LANGKAH 1: SETUP ── */}
                    {step === 'setup' && (
                        <>
                            {tabPlatform}
                            {/* Pilih pintu: Tampal Link atau Scrape Showcase */}
                            <div className="imp-modes">
                                <button className={`imp-mode${setupMode === 'link' ? ' on' : ''}`} onClick={() => setSetupMode('link')}>
                                    {T('import.mode.link', 'Tampal Link')}
                                </button>
                                <button className={`imp-mode${setupMode === 'showcase' ? ' on' : ''}`} onClick={() => setSetupMode('showcase')}>
                                    {T('import.mode.showcase', 'Scrape Showcase')}
                                </button>
                            </div>

                            {setupMode === 'link' && (
                                <>
                                    <label className="imp-lbl">{T('import.setup.linksLabel', 'Tampal link produk TikTok (satu atau banyak)')}</label>
                                    <textarea
                                        className="imp-ta"
                                        value={linksText}
                                        onChange={(e) => setLinksText(e.target.value)}
                                        placeholder={'https://shop.tiktok.com/view/product/...\nhttps://vt.tiktok.com/...'}
                                    />
                                </>
                            )}

                            {setupMode === 'showcase' && (
                                <>
                                    {!showcasePreview && (
                                        <div className="imp-note" style={{ marginTop: 0 }}>
                                            {T('import.showcase.note', 'Extension akan buka showcase Seller anda, kumpul semua produk, kemudian anda pilih yang mana nak import. Pastikan anda sudah log masuk TikTok Seller.')}
                                        </div>
                                    )}
                                    {!showcasePreview && (
                                        <>
                                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 12 }}>
                                                <div style={{ flex: 1 }}>
                                                    <label className="imp-lbl">{T('import.showcase.fromPage', 'Dari page')}</label>
                                                    <input className="imp-sel" type="number" min="1" value={showcaseStart} onChange={(e) => setShowcaseStart(e.target.value)} disabled={collecting} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label className="imp-lbl">{T('import.showcase.toPage', 'Hingga page')}</label>
                                                    <input className="imp-sel" type="number" min="1" placeholder={T('import.showcase.toPagePh', 'semua')} value={showcaseEnd} onChange={(e) => setShowcaseEnd(e.target.value)} disabled={collecting} />
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--s-ink-3)', marginTop: 6 }}>
                                                {T('import.showcase.pageHint', '1 page = 20 produk. Biar "Hingga page" kosong untuk kutip semua.')}
                                            </div>
                                        </>
                                    )}
                                    {!showcasePreview && (
                                        <button className="imp-btn primary" style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 8 }} disabled={collecting || extAlive === false} onClick={collectShowcase}>
                                            {collecting && <span className="imp-spin" style={{ borderColor: 'rgba(255,255,255,.35)', borderTopColor: 'var(--s-card)' }} />}
                                            {collecting
                                                ? T('import.showcase.collecting', `Mengumpul… page ${showcaseProgress?.page || 0} · ${showcaseProgress?.found || 0} produk`, { page: showcaseProgress?.page || 0, found: showcaseProgress?.found || 0 })
                                                : T('import.showcase.collect', 'Kutip dari Showcase')}
                                        </button>
                                    )}
                                    {collectError && (
                                        <div className="imp-cap" style={{ marginTop: 12 }}><span>⚠️</span><span>{collectError}</span></div>
                                    )}

                                    {showcasePreview && (
                                        <>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 8px' }}>
                                                <input className="imp-sel" style={{ flex: 1 }} placeholder={T('import.showcase.search', 'Cari nama produk…')} value={showcaseSearch} onChange={(e) => setShowcaseSearch(e.target.value)} />
                                                <button className="imp-btn" onClick={toggleAllFiltered}>{allFilteredSel ? T('import.showcase.none', 'Nyahpilih') : T('import.showcase.all', 'Pilih semua')}</button>
                                            </div>
                                            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                                                {showcaseFiltered.map((p) => (
                                                    <label className="imp-row" key={p.id} style={{ cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={showcaseSel.has(p.id)} onChange={() => toggleSel(p.id)} />
                                                        <span className="nm">{p.name || p.id}</span>
                                                        {p.shop && <span style={{ fontSize: 10, color: 'var(--s-ink-3)' }}>{p.shop}</span>}
                                                    </label>
                                                ))}
                                            </div>
                                            <div style={{ fontSize: 12, color: 'var(--s-ink-2)', marginTop: 6 }}>
                                                {T('import.showcase.selected', `${showcaseSel.size} dipilih daripada ${showcasePreview.length}`, { n: showcaseSel.size, total: showcasePreview.length })}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            <div className="imp-ext">
                                <span className="imp-dot" style={{ background: extAlive === true ? 'var(--s-ok)' : extAlive === false ? 'var(--s-stop)' : 'var(--s-ink-3)' }} />
                                <span style={{ color: 'var(--s-ink-2)' }}>
                                    {extAlive === true ? T('import.ext.on', 'Extension SESAAT aktif')
                                        : extAlive === false ? T('import.ext.off', 'Extension belum bersambung.')
                                            : T('import.ext.check', 'Menyemak extension…')}
                                </span>
                            </div>
                            {extAlive === false && (
                                <div className="imp-reconnect">
                                    <span>{T('import.ext.reconnect', 'Klik butang di bawah untuk sambung.')}</span>
                                    <button className="imp-refresh" onClick={() => window.location.reload()}>
                                        <span aria-hidden="true">🔄</span>{T('import.ext.refreshBtn', 'Refresh halaman')}
                                    </button>
                                </div>
                            )}
                            {setupMode === 'link' && (
                                <div className="imp-note">
                                    {T('import.setup.note', 'Extension yang buat kerja scrape di belakang tabir. Jika muncul captcha, ia akan keluar di tab TikTok untuk anda selesaikan sendiri.')}
                                </div>
                            )}
                        </>
                    )}

                    {/* ── LANGKAH 2: SCRAPE ── */}
                    {step === 'scrape' && (
                        <>
                            {captcha && (
                                <div className="imp-cap">
                                    <span>⚠️</span>
                                    <span>{captcha.message || T('import.captcha', 'Captcha muncul — sila selesaikan di tab TikTok yang terbuka. Scrape akan sambung sendiri selepas itu.')}</span>
                                </div>
                            )}
                            {/* Loading progression jelas — spinner + produk yang extension sedang proses sekarang. */}
                            <div className="imp-scraping">
                                <span className="imp-spin" />
                                <span className="nm">
                                    {currentScraping
                                        ? `${T('import.scrape.now', 'Sedang scrape: ')}${currentScraping.data?.name || currentScraping.sourceUrl}`
                                        : (done < total ? T('import.scrape.starting', 'Menyambung ke extension…') : T('import.scrape.finishing', 'Menyelesaikan…'))}
                                </span>
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 700 }}>
                                {done}<span style={{ fontSize: 13, color: 'var(--s-ink-3)', fontWeight: 500 }}> / {total}</span>
                            </div>
                            <div className="imp-prog"><div style={{ width: total ? `${Math.round(done / total * 100)}%` : '0%' }} /></div>
                            <div style={{ fontSize: 12, color: 'var(--s-ink-2)' }}>
                                ✓ {okCount} {T('import.scrape.ok', 'siap')} · ✗ {failedItems.length} {T('import.scrape.fail', 'gagal')}
                            </div>
                            <div style={{ marginTop: 12, maxHeight: 220, overflowY: 'auto' }}>
                                {results.map((r, i) => (
                                    <div className="imp-row" key={i}>
                                        <span className="nm">{r.data?.name || r.sourceUrl}</span>
                                        {r.status === 'success' ? badge('var(--s-ok-soft,var(--s-fill))', 'var(--s-ok)', '✓')
                                            : r.status === 'error' ? badge('var(--s-stop-soft,var(--s-fill))', 'var(--s-stop)', '✗')
                                                : r.status === 'scraping' ? badge('var(--s-fill)', 'var(--s-ink-2)', '…')
                                                    : badge('var(--s-fill)', 'var(--s-ink-3)', '·')}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {/* ── LANGKAH 3: REVIEW ── */}
                    {step === 'review' && (
                        <>
                            <div style={{ fontSize: 13, color: 'var(--s-ink-2)', marginBottom: 10 }}>
                                {T('import.review.summary', `${okCount} produk berjaya (${readyCount} READY). ${failedItems.length} gagal.`, { ok: okCount, ready: readyCount, fail: failedItems.length })}
                            </div>

                            {/* Bar pukal — pilih semua + tindakan atas yang ditanda */}
                            {successRows.length > 0 && (
                                <div className="imp-bulkbar">
                                    <label className="imp-checkl">
                                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                                        {selectedUrls.size > 0 ? T('import.bulk.n', `${selectedUrls.size} dipilih`, { n: selectedUrls.size }) : T('import.bulk.all', 'Pilih semua')}
                                    </label>
                                    {selectedUrls.size > 0 && (
                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                                            <button className="imp-chip brand" onClick={() => { setGroupMenuFor(groupMenuFor === '__bulk__' ? null : '__bulk__'); setShowAddGroup(false); }}>🏷 {T('import.bulk.move', 'Masuk Group')} ▾</button>
                                            <button className="imp-chip stop" onClick={() => removeRows(Array.from(selectedUrls))}>✕ {T('import.bulk.remove', 'Buang')}</button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {groupMenuFor === '__bulk__' && <div style={{ marginBottom: 8 }}>{groupMenu(Array.from(selectedUrls))}</div>}

                            {/* Senarai kad produk */}
                            <div className="imp-cards">
                                {rows.length === 0 && <div className="imp-note" style={{ marginTop: 0 }}>{T('import.review.empty', 'Tiada hasil. Balik ke Setup untuk mula.')}</div>}
                                {rows.map((r) => {
                                    const grp = allGroups.find(g => g.id === r.group);
                                    const isErr = r.status === 'error';
                                    const isBusy = r.status === 'scraping' || r.status === 'pending' || retryingUrls.has(r.sourceUrl);
                                    const img = r.data?.imageUrl || r.data?.rawImageUrl || (Array.isArray(r.data?.gallery) && r.data.gallery[0]) || '';
                                    const src = scrapeSourceUrl(r.data, r.sourceUrl);
                                    return (
                                        <div className={`imp-card${isErr ? ' err' : ''}`} key={r.sourceUrl}>
                                            <div className="imp-cardtop">
                                                {!isBusy && <input type="checkbox" checked={selectedUrls.has(r.sourceUrl)} onChange={() => toggleSelect(r.sourceUrl)} disabled={r.status !== 'success'} style={{ marginTop: 4 }} />}
                                                {img ? <img src={img} alt="" className="imp-thumb" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                                                    : <div className="imp-thumb ph">{isBusy ? '⏳' : '🖼'}</div>}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div className="imp-idrow">
                                                        <span className="imp-id" title="ID produk TikTok">#{r.productId || '—'}</span>
                                                        {r.exists && badge('var(--s-fill)', 'var(--s-ink-3)', T('import.review.exists', 'DAH ADA'))}
                                                        {r.status === 'success' && (r.ready ? badge('var(--s-ok-soft,var(--s-fill))', 'var(--s-ok)', 'READY') : badge('var(--s-warn-soft,var(--s-fill))', 'var(--s-warn)', 'PENDING'))}
                                                        {isBusy && <span style={{ fontSize: 11, color: 'var(--s-ink-3)', fontWeight: 600 }}>{T('import.card.scraping', 'Scraping…')}</span>}
                                                    </div>
                                                    <div className="imp-name">{r.data?.name || <span style={{ color: 'var(--s-ink-3)', fontWeight: 500 }}>{r.sourceUrl}</span>}</div>
                                                    {isErr ? (
                                                        <div className="imp-err">❌ {r.error || T('import.card.failed', 'Scrape gagal')}</div>
                                                    ) : r.status === 'success' && (
                                                        <div className="imp-meta">
                                                            {T('import.card.price', 'Harga')} <b style={{ color: 'var(--s-ok)' }}>{r.data?.price || '—'}</b>
                                                            {r.data?.discount ? <> · <b style={{ color: 'var(--s-stop)' }}>{r.data.discount}</b></> : null}
                                                        </div>
                                                    )}
                                                </div>
                                                {!isBusy && <button className="imp-xrow" title={T('import.card.remove', 'Buang produk ni')} onClick={() => removeRows([r.sourceUrl])}>✕</button>}
                                            </div>

                                            {r.status === 'success' && (
                                                <div className="imp-copyrow">
                                                    <button className="imp-chip" onClick={() => copyText(buildViewProductLink(r.data, r.sourceUrl), T('import.copy.view', '✓ Link produk disalin'))}>📋 {T('import.copy.viewBtn', 'Link produk')}</button>
                                                    {src && <button className="imp-chip" onClick={() => copyText(src, T('import.copy.src', '✓ Link sumber disalin'))}>📋 {T('import.copy.srcBtn', 'Link sumber')}</button>}
                                                </div>
                                            )}

                                            {isErr && (
                                                <div className="imp-erractions">
                                                    <button className="imp-chip warn" onClick={() => openEdit(r)}>✏️ {T('import.card.edit', 'Edit Manual')}</button>
                                                    <button className="imp-chip brand" disabled={retryingUrls.has(r.sourceUrl) || extAlive === false} onClick={() => retryOne(r.sourceUrl)}>↻ {T('import.card.retry', 'Retry')}</button>
                                                </div>
                                            )}

                                            {r.status === 'success' && !r.ready && (
                                                <div className="imp-warn">
                                                    <div style={{ fontWeight: 700 }}>⚠ {T('import.card.pendingWarn', 'Data tak cukup — akan simpan sebagai PENDING')}</div>
                                                    <div style={{ marginTop: 2 }}>{T('import.card.stillNeed', 'Masih perlu')}: {(r.missing || []).join(', ')}</div>
                                                    <button className="imp-chip warn" style={{ marginTop: 6 }} onClick={() => openEdit(r)}>✏️ {T('import.card.fill', 'Lengkapkan Data')}</button>
                                                </div>
                                            )}

                                            {r.status === 'success' && (
                                                <>
                                                    <button className="imp-gpill" style={grp ? { background: grp.color, color: '#fff', borderColor: 'transparent', borderStyle: 'solid' } : undefined}
                                                        onClick={() => { setGroupMenuFor(groupMenuFor === r.sourceUrl ? null : r.sourceUrl); setShowAddGroup(false); }}>
                                                        🏷 <span className="nm">{grp ? grp.name : T('import.group.pick', 'Pilih Group')}</span>
                                                        <span style={{ opacity: .7 }}>{groupMenuFor === r.sourceUrl ? '▴' : '▾'}</span>
                                                    </button>
                                                    {groupMenuFor === r.sourceUrl && groupMenu([r.sourceUrl])}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Destinasi = kad profile yang dibuka pada halaman Products.
                                BACA SAHAJA — pemilih akaun di sini dibuang (arahan user
                                2026-08-21) supaya tiada dua tempat memilih benda yang sama.
                                ⛔ Nota ini WAJIB kekal: tanpa ia pengguna langsung tidak tahu
                                produk akan mendarat di akaun mana sehingga import selesai. */}
                            <div className="imp-note" style={{ marginTop: 12 }}>
                                {namaProfilDestinasi
                                    ? T('import.review.dest', `Produk akan disimpan ke akaun: ${namaProfilDestinasi}`, { nama: namaProfilDestinasi })
                                    : T('import.review.destNone', 'Produk akan disimpan tanpa akaun — ia duduk dalam "Semua produk". Buka satu kad akaun dahulu kalau mahu ia terus masuk ke akaun itu.')}
                            </div>

                            {savedMsg && <div className="imp-note" style={{ marginTop: 12 }}>{savedMsg}</div>}
                        </>
                    )}
                </div>

                <div className="imp-foot">
                    {step === 'setup' && (() => {
                        const n = setupMode === 'link' ? parseLinks(linksText).length : showcaseSel.size;
                        return (
                            <>
                                <div style={{ flex: 1, fontSize: 12, color: 'var(--s-ink-3)' }}>
                                    {setupMode === 'link'
                                        ? `${n} ${T('import.setup.count', 'link dikesan')}`
                                        : `${n} ${T('import.showcase.chosen', 'produk dipilih')}`}
                                </div>
                                <button className="imp-btn" onClick={() => onClose?.()}>{T('import.cancel', 'Batal')}</button>
                                <button className="imp-btn primary" disabled={n === 0 || extAlive === false || collecting} onClick={startScrape}>
                                    {T('import.start', 'Mula scrape')} →
                                </button>
                            </>
                        );
                    })()}
                    {step === 'scrape' && (
                        <>
                            <div style={{ flex: 1, fontSize: 12, color: 'var(--s-ink-3)' }}>{T('import.scrape.running', 'Extension sedang bekerja…')}</div>
                            <button className="imp-btn" onClick={cancelScrape}>{T('import.scrape.stop', 'Berhenti')}</button>
                        </>
                    )}
                    {step === 'review' && (
                        <>
                            <button className="imp-btn" onClick={() => setStep('setup')}>{T('import.review.back', '← Import lagi')}</button>
                            <div style={{ flex: 1 }} />
                            <button className="imp-btn ok" disabled={saving || successRows.length === 0} onClick={handleSave}>
                                {saving ? T('import.saving', 'Menyimpan…') : T('import.save', `Simpan ${successRows.length} kad`, { n: successRows.length })}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* ── Modal Edit Manual (isi/betulkan data produk) ── */}
            {editUrl && (() => {
                const canSave = editFields.name?.trim() && editFields.price?.trim();
                const pid = extractTikTokProductId(editUrl) || '';
                const link = pid ? `https://shop.tiktok.com/view/product/${pid}` : editUrl;
                const prev = evaluateProductData(
                    { description: editFields.description, price: editFields.price, category: editFields.category, specifications: parseSpecLines(editFields.specsText || ''), topReviews: parseReviewLines(editFields.reviewsText || '') },
                    { name: editFields.name, imageUrl: editFields.imageUrl, link }
                );
                const F = (key, label, ph) => (
                    <div>
                        <label className="imp-fldl">{label}</label>
                        <input className="imp-fld" value={editFields[key] || ''} onChange={e => setEditFields(p => ({ ...p, [key]: e.target.value }))} placeholder={ph} />
                    </div>
                );
                return (
                    <div className="imp-editovl" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditUrl(null); }}>
                        <div className="imp-editbox">
                            <div className="imp-edithead">
                                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--s-ink)' }}>✏️ {T('import.edit.title', 'Edit Produk Manual')}</div>
                                <div style={{ fontSize: 11, color: 'var(--s-ink-3)', marginTop: 2 }}>{T('import.edit.hint', 'Isi data produk sendiri. Nama, Harga & Description penting untuk jadi READY.')}</div>
                            </div>
                            <div className="imp-editbody">
                                {F('name', T('import.edit.name', 'Nama Produk *'), 'cth: PENCUCI PINKY WASH')}
                                {F('price', T('import.edit.price', 'Harga *'), 'cth: RM13.80')}
                                {F('originalPrice', T('import.edit.orig', 'Harga Asal'), 'cth: RM27.60')}
                                {F('discount', T('import.edit.disc', 'Diskaun %'), 'cth: -50%')}
                                {F('category', T('import.edit.cat', 'Kategori'), 'cth: Beauty & Personal Care')}
                                {F('imageUrl', T('import.edit.img', 'Image URL'), 'https://…')}
                                <div>
                                    <label className="imp-fldl">{T('import.edit.desc', 'Description *')}</label>
                                    <textarea className="imp-fld" style={{ minHeight: 80, resize: 'vertical' }} rows={4} value={editFields.description || ''} onChange={e => setEditFields(p => ({ ...p, description: e.target.value }))} placeholder={T('import.edit.descPh', 'Apa produk ni, kelebihannya, bahan, cara guna…')} />
                                </div>
                                <div>
                                    <label className="imp-fldl">{T('import.edit.spec', 'Specification')}</label>
                                    <textarea className="imp-fld" style={{ minHeight: 60, resize: 'vertical' }} rows={3} value={editFields.specsText || ''} onChange={e => setEditFields(p => ({ ...p, specsText: e.target.value }))} placeholder={'Bahan : Kapas\nSaiz : M, L, XL'} />
                                </div>
                                <div>
                                    <label className="imp-fldl">{T('import.edit.rev', 'Customer Reviews')}</label>
                                    <textarea className="imp-fld" style={{ minHeight: 60, resize: 'vertical' }} rows={3} value={editFields.reviewsText || ''} onChange={e => setEditFields(p => ({ ...p, reviewsText: e.target.value }))} placeholder={'Barang sampai cepat, kualiti terbaik\nAnak suka sangat'} />
                                </div>
                                <div style={{ background: prev.ready ? 'var(--s-ok-soft,var(--s-fill))' : 'var(--s-warn-soft,var(--s-fill))', border: `1px solid ${prev.ready ? 'var(--s-ok)' : 'var(--s-warn)'}`, borderRadius: 8, padding: '9px 11px' }}>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: prev.ready ? 'var(--s-ok)' : 'var(--s-warn)' }}>
                                        {prev.ready ? T('import.edit.ok', '✓ Cukup — produk ini akan jadi READY') : T('import.edit.notReady', '⚠ Belum cukup untuk READY')}
                                    </div>
                                    {!prev.ready && <div style={{ fontSize: 11, color: 'var(--s-ink-2)', marginTop: 3 }}>{T('import.card.stillNeed', 'Masih perlu')}: {prev.missing.join(', ')}</div>}
                                </div>
                            </div>
                            <div className="imp-editfoot">
                                <button className="imp-btn" onClick={() => setEditUrl(null)}>{T('import.cancel', 'Batal')}</button>
                                <button className="imp-btn ok" disabled={!canSave} onClick={saveEdit}>✓ {T('import.edit.save', 'Simpan')}</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {rowToast && <div className="imp-toast">{rowToast}</div>}
        </div>
    );
}
